import { Button, Spinner } from "@deligoo/ui"
import clsx from "clsx"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useHistory } from "react-router-dom"

import { useAppSelector } from "@/App"
import { Main } from "@/components/AppLayout"
import { selectCount } from "@/store"
import { selectAreAllSelectedTeamsStatsLoaded, selectMultiTeamsTotalDeliveryOrdersCount } from "@/store/teamsStats"
import { useSaveUserSettings, useUserSettings } from "@/utils"

import { MultiTeamSelectModal } from "../MultiTeamSelectModal"
import { StatisticsFilterModal } from "../StatisticsFilterModal"
import cls from "./EventsList.module.scss"
import { TeamsList } from "./TeamsList"

export const EVENTS_TABLE_HEADERS = [
  "name",
  "active_drivers_count",
  "blocked_drivers_count",
  "today_orders_count",
  "load_value",
  "late_drivers_count",
  "today_delivery_orders_count",
  "unsettled_payments_count",
  "dynamic_buffer_real_value",
  "traffic_value",
  "omitted_orders_count",
  "weather",
] as const

export type EventsTableHeader = typeof EVENTS_TABLE_HEADERS[number]

type Settings = {
  hiddenColumns: EventsTableHeader[]
}

export const EventsList = () => {
  const selectedTeamsCount = useAppSelector(selectCount((state) => state.teams.currentMulti))
  const totalDeliveryOrdersCount = useAppSelector(selectMultiTeamsTotalDeliveryOrdersCount)
  const areAllStatsLoaded = useAppSelector(selectAreAllSelectedTeamsStatsLoaded)

  const settings = useUserSettings<Settings>("events_list_settings")

  const [isTeamsSelectOpen, setTeamsSelectOpen] = useState(false)
  const [isStatisticsFilterModalOpen, setStatisticsFilterModalOpen] = useState(false)
  const [hiddenColumns, setHiddenColumns] = useState<EventsTableHeader[]>(settings?.hiddenColumns || [])

  useSaveUserSettings<Settings>("events_list_settings", { hiddenColumns })

  const { push } = useHistory()

  const { t } = useTranslation()

  return (
    <Main wrapperClassName={cls.eventsListPage} overflowHidden="md" page>
      <nav className={cls.nav}>
        <div className={cls.controls}>
          <Button
            color="light"
            size="large"
            className={clsx(cls.navButton, cls.teams)}
            iconProps={{ name: "team", color: "primary" }}
            onClick={() => setTeamsSelectOpen(true)}
            pill
          >
            {`${selectedTeamsCount} ${t("common:teams", {
              count: selectedTeamsCount,
            })}`}
          </Button>

          <Button
            color="light"
            size="large"
            className={clsx(cls.navButton, "hide-mobile")}
            iconProps={{ name: "filters", color: "primary" }}
            onClick={() => setStatisticsFilterModalOpen(true)}
            pill
          />
        </div>

        <div className={clsx(cls.controls, "hide-mobile")}>
          <div className={cls.totalOrders}>
            <span>
              {t("total_orders")}: <strong>{totalDeliveryOrdersCount || 0}</strong>
            </span>
            {areAllStatsLoaded || <Spinner className="ml-2" size="small" inline />}
          </div>
        </div>

        <div className={cls.controls}>
          <span className={cls.viewType}>{t("view_type")}:</span>

          <Button
            color="light"
            size="large"
            iconSide="right"
            iconProps={{ name: "grid", color: "primary" }}
            className={clsx(cls.navButton, "hide-mobile")}
            onClick={() => push("/centrum-zdarzen")}
            pill
          >
            {t("grid")}
          </Button>

          <Button
            color="light"
            size="large"
            iconSide="right"
            iconProps={{ name: "statistics", color: "primary" }}
            className={cls.navButton}
            onClick={() => push("/centrum-zdarzen/statystyki")}
            pill
          >
            {t("statistics")}
          </Button>

          <Button
            color="primary"
            size="large"
            iconSide="right"
            iconProps={{ name: "list-3" }}
            className={clsx(cls.navButton, cls.active)}
            pill
          >
            {t("list")}
          </Button>
        </div>
      </nav>

      <div className={clsx(cls.controls, cls.amount, "hide-desktop")}>
        <div className={cls.totalOrders}>
          <span>
            {t("total_orders")}: <strong>{totalDeliveryOrdersCount || 0}</strong>
          </span>
          {areAllStatsLoaded || <Spinner className="ml-2" size="small" inline />}
        </div>
      </div>

      <TeamsList hiddenColumns={hiddenColumns} />

      {isTeamsSelectOpen && <MultiTeamSelectModal setIsOpen={setTeamsSelectOpen} />}

      {isStatisticsFilterModalOpen && (
        <StatisticsFilterModal
          handleClose={() => setStatisticsFilterModalOpen(false)}
          hiddenColumns={hiddenColumns}
          setHiddenColumns={setHiddenColumns}
        />
      )}
    </Main>
  )
}
